import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import UserAvatar from '../../../components/UserAvatar';

const QuizResults = ({ 
  totalQuestions, 
  correctAnswers, 
  totalXP, 
  badgesEarned, 
  recommendations,
  aiInsights,
  recommendedDifficulty,
  userId,
  userName,
  onBadgeCelebrate,
}) => {
  const navigate = useNavigate();
  const percentage = Math.round((correctAnswers / totalQuestions) * 100);
  const celebratedRef = useRef(false);

  // Auto-trigger celebration for the first badge earned on results screen
  useEffect(() => {
    if (celebratedRef?.current) return;
    if (badgesEarned?.length > 0 && onBadgeCelebrate) {
      celebratedRef.current = true;
      const t = setTimeout(() => onBadgeCelebrate(badgesEarned?.[0]), 500);
      return () => clearTimeout(t);
    }
  }, [badgesEarned, onBadgeCelebrate]);

  const getPerformanceMessage = () => {
    if (percentage >= 90) return "Outstanding Performance!";
    if (percentage >= 75) return "Great Job!";
    if (percentage >= 60) return "Good Effort!";
    return "Keep Practicing!";
  };

  const getPerformanceColor = () => {
    if (percentage >= 90) return "text-success";
    if (percentage >= 75) return "text-primary";
    if (percentage >= 60) return "text-warning";
    return "text-muted-foreground";
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8">
      {/* Hero section with avatar */}
      <div className="text-center mb-8">
        <div className="flex flex-col items-center gap-4 mb-6">
          <UserAvatar
            userId={userId}
            name={userName}
            size="w-24 h-24"
            textSize="text-3xl"
            className="ring-4 ring-primary/30 shadow-lg"
          />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-1">
              {getPerformanceMessage()}
            </h1>
            {userName && (
              <p className="text-base text-muted-foreground">
                {userName}
              </p>
            )}
            <p className="text-lg text-muted-foreground mt-1">
              You've completed the quiz
            </p>
          </div>
        </div>

        {/* Score ring */}
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-4 border-primary/30 bg-primary/5 mb-2">
          <span className={`text-2xl font-bold ${getPerformanceColor()}`}>{percentage}%</span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <Icon name="CheckCircle" size={32} className="text-success mx-auto mb-3" />
          <p className="text-3xl font-bold text-foreground mb-1">{correctAnswers}/{totalQuestions}</p>
          <p className="text-sm text-muted-foreground">Correct Answers</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <Icon name="Zap" size={32} className="text-secondary mx-auto mb-3" />
          <p className="text-3xl font-bold text-foreground mb-1">+{totalXP}</p>
          <p className="text-sm text-muted-foreground">XP Earned</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <Icon name="Award" size={32} className="text-accent mx-auto mb-3" />
          <p className="text-3xl font-bold text-foreground mb-1">{badgesEarned?.length ?? 0}</p>
          <p className="text-sm text-muted-foreground">Badges Unlocked</p>
        </div>
      </div>

      {/* Badges */}
      {badgesEarned?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Award" size={24} className="text-accent" />
            Badges Earned
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {badgesEarned?.map((badge, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-accent/5 border border-accent/20">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Award" size={24} className="text-accent" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{badge?.name}</p>
                  <p className="text-sm text-muted-foreground">{badge?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Lightbulb" size={24} className="text-warning" />
            Recommended Next Steps
          </h2>
          <div className="space-y-3">
            {recommendations?.map((rec, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-200">
                <Icon name="ArrowRight" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {aiInsights && (
        <div className="mt-8 space-y-6">
          <h3 className="text-2xl font-bold">🤖 AI Performance Analysis</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="font-semibold text-lg mb-3">Comprehension Level</h4>
              <p className="text-gray-700">{aiInsights?.comprehensionLevel}</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="font-semibold text-lg mb-3">Time Management</h4>
              <p className="text-gray-700">{aiInsights?.timeManagementAssessment}</p>
            </div>
          </div>

          {aiInsights?.strengthAreas && aiInsights?.strengthAreas?.length > 0 && (
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <h4 className="font-semibold text-lg mb-3 text-green-800">✨ Your Strengths</h4>
              <ul className="space-y-2">
                {aiInsights?.strengthAreas?.map((strength, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {aiInsights?.weaknessAreas && aiInsights?.weaknessAreas?.length > 0 && (
            <div className="bg-amber-50 p-6 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-lg mb-3 text-amber-800">📚 Areas for Improvement</h4>
              <ul className="space-y-2">
                {aiInsights?.weaknessAreas?.map((weakness, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-amber-600 mr-2">→</span>
                    <span className="text-gray-700">{weakness}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {aiInsights?.improvementSuggestions && (
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-lg mb-3 text-blue-800">💡 Personalized Suggestions</h4>
              <ul className="space-y-2">
                {aiInsights?.improvementSuggestions?.map((suggestion, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {recommendedDifficulty && (
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-lg mb-3 text-purple-800">🎯 Next Challenge</h4>
              <p className="text-gray-700">
                Based on your performance, we recommend trying a{' '}
                <strong className="text-purple-800 uppercase">{recommendedDifficulty}</strong>
                {' '}level quiz next to continue your growth.
              </p>
              {aiInsights?.performancePatterns && (
                <div className="mt-4 space-y-2 text-sm text-gray-600">
                  <p>Consistency Score: {Math.round(aiInsights?.performancePatterns?.consistencyScore * 100)}%</p>
                  <p>Learning Style: {aiInsights?.performancePatterns?.learningStyle}</p>
                  <p>Progress Rate: {aiInsights?.performancePatterns?.progressionRate}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mt-8">
        <Button
          variant="outline"
          size="lg"
          fullWidth
          iconName="RotateCcw"
          onClick={() => window.location?.reload()}
        >
          Retake Quiz
        </Button>
        <Button
          variant="default"
          size="lg"
          fullWidth
          iconName="Home"
          onClick={() => navigate('/student-stage-dashboard')}
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
};

export default QuizResults;